import { useEffect, useState } from 'react';
import { useForm, UseFormReturnType } from '@mantine/form';
import { FieldWithValue, FormValuesType } from 'src/UI/dynamicUI/types';
import { useParams, useSearchParams } from 'react-router';
import {
  FormAccordion,
  FormAccordionItem,
  FormAccordionItemList,
  ReadOnlyWrapper,
} from 'src/UI/dynamicUI/layout/DynamicEntitySection';
import { Box, Grid } from '@mantine/core';
import styles from 'src/financial/financial-data/financial-item/forms/common/FormLayout.module.css';
import {
  getFinancialFormSetupValues,
  ProductFormComponentProps,
  updateFeedInfoAfterAssetCreation,
} from 'src/financial/financial-data/financial-item/forms/common/FinancialFormHelpers';
import { useDynamicUI } from 'src/UI/dynamicUI/DynamicUiContext';
import { DynamicUiProviderWrapper } from 'src/financial/financial-data/financial-item/forms/common/DynamicUiProviderWrapper';
import { FinancialFormField } from 'src/financial/financial-data/financial-item/forms/common/FinancialFormField';
import { GridPair } from 'src/financial/financial-data/financial-item/forms/common/GridPair';
import {
  FeedInfo,
  FinancialFormContainer,
} from 'src/financial/financial-data/financial-item/forms/common/FinancialFormContainer';
import {
  FormTitleWithConnectedDataBadge,
  useConnectedDataIcon,
} from 'src/financial/financial-data/financial-item/forms/common/ConnectedDataBadge';
import {
  useValidateProviderReference,
  validateProviderReferenceAndSetError,
} from 'src/financial/financial-data/hooks/useValidateProviderReference';

const PENSION_ANNUITY_PRODUCT = 'PensionAnnuity';
const PENSION_ANNUITY_BREAKDOWN_ITEM = 'PensionAnnuityBreakdownItem';

export function PensionAnnuity(props: ProductFormComponentProps) {
  const { onSave } = props;
  const [searchParams] = useSearchParams();
  const { clientId = '' } = useParams<{ clientId: string }>();
  const { validateProviderReference } = useValidateProviderReference(clientId);

  const {
    isCreateMode,
    uiData,
    initialValues,
    onDeleteConfirm,
    feedInfo: initialFeedInfo,
    onConnectAsset,
  } = getFinancialFormSetupValues(PENSION_ANNUITY_PRODUCT, props, [
    { product: PENSION_ANNUITY_BREAKDOWN_ITEM, field: 'breakdown_items' },
  ]);

  const [feedInfo, setFeedInfo] = useState<FeedInfo>(initialFeedInfo);

  const form = useForm<FormValuesType>({
    mode: 'uncontrolled',
    initialValues: initialValues,
  });

  useEffect(() => {
    const providerReference = searchParams.get('providerReference');
    if (providerReference && isCreateMode) {
      form.setFieldValue(`${PENSION_ANNUITY_PRODUCT}.provider_reference_number`, providerReference);
    }
  }, [searchParams, isCreateMode, form]);

  const handleSave = async (values: FormValuesType) => {
    if (
      PENSION_ANNUITY_BREAKDOWN_ITEM in values &&
      Array.isArray(values[PENSION_ANNUITY_BREAKDOWN_ITEM])
    ) {
      const annuityData = values[PENSION_ANNUITY_PRODUCT] as Record<string, unknown>;

      const isProviderReferenceValid = await validateProviderReferenceAndSetError(
        annuityData,
        clientId,
        form,
        PENSION_ANNUITY_PRODUCT,
        validateProviderReference,
      );
      if (!isProviderReferenceValid) {
        return;
      }

      const mappedValues = {
        ...annuityData,
        breakdown_items: values[PENSION_ANNUITY_BREAKDOWN_ITEM].map(item => ({ ...item })),
      };

      await onSave(mappedValues, uiData.ui.entitiesMetadata[PENSION_ANNUITY_PRODUCT]);

      updateFeedInfoAfterAssetCreation(form, PENSION_ANNUITY_PRODUCT, isCreateMode, setFeedInfo);
    }
  };

  const handleAddBreakdownItem = () => {
    form.insertListItem(PENSION_ANNUITY_BREAKDOWN_ITEM, { type: null, amount: null });
  };

  const untypedItems = form.getValues()[PENSION_ANNUITY_BREAKDOWN_ITEM];
  const breakdownItems = Array.isArray(untypedItems) ? untypedItems : [];

  return (
    <DynamicUiProviderWrapper
      form={form}
      uiData={uiData}
      onDeleteConfirm={isCreateMode ? () => {} : onDeleteConfirm}
      isCreateMode={isCreateMode}
    >
      <form onSubmit={form.onSubmit(handleSave)} autoComplete={'off'}>
        <FormContent
          breakdownItems={breakdownItems}
          handleAddBreakdownItemPress={handleAddBreakdownItem}
          form={form}
          feedInfo={feedInfo}
          onConnect={onConnectAsset}
        />
      </form>
    </DynamicUiProviderWrapper>
  );
}

type FormContentProps = {
  breakdownItems: FieldWithValue[];
  handleAddBreakdownItemPress: () => void;
  form: UseFormReturnType<FormValuesType>;
  feedInfo: FeedInfo;
  onConnect?: (id: string) => void;
};

const FormContent = ({
  breakdownItems,
  handleAddBreakdownItemPress,
  form,
  feedInfo,
  onConnect,
}: FormContentProps) => {
  const { isEditing } = useDynamicUI();
  const connectedDataIcon = useConnectedDataIcon(feedInfo.isUpdatedAutomatically);

  const summaryContent = (
    <>
      <Grid>
        <GridPair first={<AnnuityPensionField field="description" />} />
        <GridPair
          isHighlighted
          first={<AnnuityPensionField field="provider" />}
          second={<AnnuityPensionField field="provider_reference_number" />}
        />
      </Grid>
      <Grid>
        <GridPair
          isHighlighted
          first={<AnnuityPensionField field="value" />}
          second={<AnnuityPensionField field="value_date" />}
        />
        <GridPair
          first={<AnnuityPensionField field="retirement_date" />}
          second={<AnnuityPensionField field="is_serviced_by_advisor" forceEditable />}
        />
        <GridPair fullWidth first={<AnnuityPensionField field="notes" />} />
      </Grid>
    </>
  );

  const titleText = 'Assets > Pensions > Annuity';
  const titleWithBadge = (
    <FormTitleWithConnectedDataBadge
      title={titleText}
      showBadge={feedInfo.isUpdatedAutomatically}
    />
  );

  return (
    <FinancialFormContainer title={titleWithBadge} feedInfo={feedInfo} onConnect={onConnect}>
      <FormAccordion defaultValue={['summary', 'total', 'breakdown']}>
        <FormAccordionItem value="summary" label="Summary" titleIcon={connectedDataIcon}>
          {feedInfo.isUpdatedAutomatically ? (
            <ReadOnlyWrapper>{summaryContent}</ReadOnlyWrapper>
          ) : (
            summaryContent
          )}
        </FormAccordionItem>

        <FormAccordionItem value="total" label="Total pension (per annum for amounts and rates)">
          <Grid>
            <GridPair
              isHighlighted
              first={<AnnuityPensionField field="annual_amount" label="Amount" />}
              second={
                <AnnuityPensionField
                  field="annual_amount_applicable_date"
                  label="Applicable date"
                />
              }
            />
            <GridPair
              isHighlighted
              first={
                <AnnuityPensionField
                  field="payment_increase_percentage"
                  label="Increase in payment"
                />
              }
              second={
                <AnnuityPensionField
                  field="payment_increase_percentage_cap"
                  label="Increase in payment cap"
                />
              }
            />
          </Grid>
          <Grid>
            <GridPair first={<AnnuityPensionField field="dependant_fraction_percentage" />} />
          </Grid>
        </FormAccordionItem>

        <FormAccordionItemList
          value="breakdown"
          label="Pension breakdown (per annum for amounts and rates)"
          counterLabel="Breakdown"
          button={{
            label: 'Add breakdown item',
            onClick: handleAddBreakdownItemPress,
          }}
          isEditing={isEditing}
          form={form}
          entityType={PENSION_ANNUITY_BREAKDOWN_ITEM}
        >
          {breakdownItems.map((_, index) => {
            return (
              <Box key={index} className={styles.breakdownItem}>
                <Grid>
                  <GridPair first={<PensionBreakdownItemField field="type" index={index} />} />
                  <GridPair
                    isHighlighted
                    first={
                      <PensionBreakdownItemField
                        field="annual_amount"
                        index={index}
                        label="Amount"
                      />
                    }
                    second={
                      <PensionBreakdownItemField
                        field="annual_amount_applicable_date"
                        index={index}
                        label="Applicable date"
                      />
                    }
                  />
                </Grid>
                <Grid>
                  <GridPair
                    isHighlighted
                    first={
                      <PensionBreakdownItemField
                        field="payment_increase_percentage"
                        index={index}
                        label="Increase in payment"
                      />
                    }
                    second={
                      <PensionBreakdownItemField
                        field="payment_increase_percentage_cap"
                        index={index}
                        label="Increase in payment cap"
                      />
                    }
                  />

                  <GridPair
                    first={
                      <PensionBreakdownItemField
                        index={index}
                        field="dependant_fraction_percentage"
                        label="Dependant's fraction"
                      />
                    }
                  />
                </Grid>
              </Box>
            );
          })}
        </FormAccordionItemList>
      </FormAccordion>
    </FinancialFormContainer>
  );
};

type AnnuityPensionFieldProps = {
  label?: string;
  field: string;
  forceEditable?: boolean;
};

const AnnuityPensionField = ({ label, field, forceEditable }: AnnuityPensionFieldProps) => (
  <FinancialFormField
    label={label}
    field={field}
    product={PENSION_ANNUITY_PRODUCT}
    forceEditable={forceEditable}
  />
);

type PensionBreakdownItemFieldProps = {
  label?: string;
  field: string;
  index: number;
};

const PensionBreakdownItemField = ({ label, field, index }: PensionBreakdownItemFieldProps) => (
  <FinancialFormField
    label={label}
    field={field}
    product={PENSION_ANNUITY_BREAKDOWN_ITEM}
    index={index}
  />
);
